import { useEffect } from 'react';
import { ThumbsDown, X } from 'lucide-react';
import type { RallyTrend } from '../services/requests';
import type { RallyChoice } from '../services/crowdRally';
import type { PushPermissionState } from '../services/pushNotifications';
import { rallyProgress, rallyTrendView, whatItTakes, type RallyGoal, type RallyTone } from '../utils/crowdRally';

export type RallyDetailReason = 'detail' | 'joined' | 'flip' | 'almost';

interface RallyDetailModalProps {
  song: { id: string; title: string; artist: string };
  sharePercent: number;
  returnPercent: number;
  trend: RallyTrend | null | undefined;
  choice: RallyChoice | undefined;
  goal: RallyGoal;
  reason: RallyDetailReason;
  pushState: PushPermissionState;
  saving?: boolean;
  onChoose: (choice: RallyChoice) => void;
  onEnableAlerts: () => void;
  onClose: () => void;
}

const TONE_CLASSES: Record<RallyTone, string> = {
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  good: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const BAR_CLASSES: Record<RallyTone, string> = {
  neutral: 'bg-gray-400 dark:bg-gray-500',
  good: 'bg-green-500',
  bad: 'bg-red-500',
};

const HEADINGS: Record<RallyDetailReason, (label: string) => string | null> = {
  detail: () => null,
  joined: () => "You're in.",
  flip: label => `Update: ${label}`,
  almost: () => 'Almost back',
};

// Detail view for one voted-out song (ITER-190): where it stands, how it can come back, and the opt in/out
export function RallyDetailModal({
  song,
  sharePercent,
  returnPercent,
  trend,
  choice,
  goal,
  reason,
  pushState,
  saving = false,
  onChoose,
  onEnableAlerts,
  onClose,
}: RallyDetailModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const view = rallyTrendView(trend, goal);
  const progress = rallyProgress(trend, sharePercent, returnPercent);
  const heading = HEADINGS[reason](view.label);
  const takes = whatItTakes(trend, returnPercent);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="rally-detail-modal">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Voted out: ${song.title}`}
      >
        <div className="flex justify-end p-2">
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pb-6 space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <ThumbsDown className="w-10 h-10 mx-auto text-red-500" />
          {heading && <p className="text-base font-semibold text-gray-900 dark:text-white text-center">{heading}</p>}
          <div className="space-y-1">
            <p className="font-medium text-gray-900 dark:text-white"><em>{song.title}</em></p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{song.artist}</p>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span>{Math.round(sharePercent)}% against · back under {returnPercent}%</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_CLASSES[view.tone]}`} data-testid="rally-trend-pill">
              {view.label}
            </span>
          </div>
          <div
            className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
            role="progressbar"
            aria-label="Progress toward coming back"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
          >
            <div className={`h-full ${BAR_CLASSES[view.tone]}`} style={{ width: `${Math.round(progress * 100)}%` }} data-testid="rally-progress-fill" />
          </div>
          {takes && (
            <p data-testid="rally-what-it-takes">
              <strong className="text-gray-900 dark:text-white">Right now:</strong> {takes}
            </p>
          )}
          <p>
            <strong className="text-gray-900 dark:text-white">How it comes back:</strong> It comes back when fewer than {returnPercent}% are against it. Two things bring the % down: new people joining in (anyone who takes part and doesn&apos;t downvote it), or people who downvoted it changing their mind. Your upvote and ranking won&apos;t bring it back, but once it&apos;s back they decide how soon it plays.
          </p>
          {choice === 'in' && (
            <p>
              <strong className="text-gray-900 dark:text-white">How you can help:</strong> get friends who aren&apos;t taking part yet to join in.
            </p>
          )}
          {reason === 'joined' && pushState === 'default' && (
            <button type="button" onClick={onEnableAlerts} className="btn btn-primary w-full">
              Want an alert when it&apos;s almost back?
            </button>
          )}
          {choice === 'in' && pushState === 'denied' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Alerts are blocked in this browser. You&apos;ll still see updates here while this page is open.
            </p>
          )}
          <div className="space-y-2">
            {choice !== 'in' && (
              <button type="button" onClick={() => onChoose('in')} disabled={saving} className="btn btn-primary w-full">
                Help bring it back
              </button>
            )}
            {choice !== 'dontCare' && (
              <button type="button" onClick={() => onChoose('dontCare')} disabled={saving} className="btn btn-secondary w-full">
                Don&apos;t tell me about this song again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
